import {
  Grid,
  Card,
  CardContent,
  Tooltip,
  IconButton,
  Typography,
  Stack,
  Button,
  TextField,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Reports } from "../../../../../app/slices/reportsSlice";
import { setCityTable } from "../../../../../app/slices/provincesSlice";
import dayjs from "dayjs";
import DateRangepicker from "../../../../../helpers/dateRangePicker";
import { MdOutlineClear } from "react-icons/md";
import { BsFillFileEarmarkExcelFill } from "react-icons/bs";
import { SelectProvince } from "../../../../../components/Selectors/SelectProvince";
import { SelectCity } from "../../../../../components/Selectors/SelectCity";
import { SelectRepresentative as SelectRep } from "../../../../../components/Selectors/SelectRepresentative";

const PosEPaymentsTable = (isFetchData) => {
  const _PosEPayments = useSelector((state) => state.reports.posEPayments);
  const loading = useSelector((state) => state.reports.reports.loading);
  const _provinces = useSelector((state) => state.provinces.provinces.data);
  const _Cities = useSelector((state) => state.provinces.cities.data);
  const repsListData = useSelector((state) => state.user.usersList.data);

  const dispatch = useDispatch();

  const [search, setSearch] = useState({
    start_date: dayjs().format("YYYY-MM-01"),
    end_date: dayjs().format("YYYY-MM-DD"),
    j_rep_id: null,
    search: "",
    is_debit: null,
    province_id: null,
    city_id: [],
  });

  const [provinceId, setProvinceId] = useState(null);

  const columns = [
    {
      field: "id",
      headerName: "#",
      flex: 0.2,
      sortable: false,
      valueGetter: (params) =>
        (params.api?.getRowIndexRelativeToVisibleRows?.(params.id) ?? 0) + 1,
    },
    {
      field: "pos_name",
      headerName: "اسم النقطة",
      flex: 1,
      renderCell: (params) => (
        <Stack>
          <Typography>{params.row.pos_name}</Typography>
          <Typography>{params.row.mobile}</Typography>
        </Stack>
      ),
    },
    {
      field: "pos_number",
      headerName: "رقم النقطة",
      flex: 1,
    },
    {
      field: "owner",
      headerName: "اسم المالك",
      flex: 1,
    },
    {
      field: "mobile",
      headerName: "رقم الهاتف",
      flex: 1,
    },
    {
      field: "j_rep_name",
      headerName: "اسم المندوب",
      flex: 1,
    },
    {
      field: "total_balance",
      headerName: "الرصيد الكلي (IQD)",
      flex: 1,
      renderCell: (params) => {
        return (
          <Typography>
            {parseFloat(params.row.total_balance)?.toLocaleString()}
          </Typography>
        );
      },
    },
    {
      field: "total_dept",
      headerName: "إجمالي الدين (IQD)",
      flex: 1,
      renderCell: (params) => {
        return (
          <Typography>
            {parseFloat(params.row.total_dept)?.toLocaleString()}
          </Typography>
        );
      },
    },
    {
      field: "total_j_balance",
      headerName: "الجملة (IQD)",
      flex: 1,
      renderCell: (params) => {
        return (
          <Typography>
            {parseFloat(params.row.total_j_balance)?.toLocaleString()}
          </Typography>
        );
      },
    },
    {
      field: "total_amount",
      headerName: "المبلغ الكلي (IQD)",
      flex: 1,
      renderCell: (params) => {
        return (
          <Typography>
            {parseFloat(params.row.total_amount)?.toLocaleString()}
          </Typography>
        );
      },
    },
    {
      field: "transaction_count",
      headerName: "عدد المعاملات",
      flex: 1,
      renderCell: (params) => {
        return <Typography>{params.row.transaction_count}</Typography>;
      },
    },
  ];

  const handleSearch = () => {
    dispatch(
      Reports.getPosEPayments({
        start_date: search.start_date,
        end_date: search.end_date,
        j_rep_id: search.j_rep_id,
        search: search.search,
        is_debit: search.is_debit,
        province_id: search.province_id,
        city_id: search.city_id,
      }),
    );
  };

  useEffect(() => {
    if (isFetchData) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle province change to update cities
  useEffect(() => {
    const selectedCities = _provinces?.filter((x) =>
      x.id === provinceId ? x.city : null,
    )[0];
    if (selectedCities) {
      dispatch(setCityTable(selectedCities?.city ?? []));
    }
    // Reset city selection when province changes
    setSearch((prev) => ({
      ...prev,
      city_id: [],
    }));
  }, [provinceId, _provinces, dispatch]);

  return (
    <Card>
      <CardContent>
        <Grid container rowSpacing={1} columnSpacing={2}>
          <Grid item xs={12} md={2}>
            <TextField
              size="small"
              value={search.search}
              onChange={(e) => {
                setSearch({
                  ...search,
                  search: e.target.value,
                });
              }}
              label="بحث عن اسم النقطة او رقمها"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <SelectRep
              onChange={(event, value) => {
                setSearch({
                  ...search,
                  j_rep_id: value ? value.id : null,
                });
              }}
              value={
                repsListData?.filter((x) => x.id === search.j_rep_id)[0] ?? null
              }
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <SelectProvince
              onChange={(e, newValue) => {
                setProvinceId(newValue ? newValue.id : null);
                setSearch({
                  ...search,
                  province_id: newValue ? newValue.id : null,
                });
              }}
              value={_provinces?.filter((x) => x.id === provinceId)[0] ?? null}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <SelectCity
              multiple
              onChange={(e, newValue) => {
                setSearch({
                  ...search,
                  city_id: newValue ? newValue.map((city) => city.id) : [],
                });
              }}
              value={
                _Cities?.filter((x) => search.city_id.includes(x.id)) ?? []
              }
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <DateRangepicker
              startDate={search.start_date}
              endDate={search.end_date}
              handleOnChange={(searchDate) => {
                const updatedFilter = {
                  from: dayjs(searchDate[0].startDate).format("YYYY-MM-DD"),
                  to: dayjs(searchDate[0].endDate).format("YYYY-MM-DD"),
                };
                if (
                  updatedFilter.from !== searchDate.startDate ||
                  updatedFilter.to !== searchDate.endDate
                ) {
                  setSearch({
                    ...search,
                    start_date: dayjs(searchDate[0].startDate).format(
                      "YYYY-MM-DD",
                    ),
                    end_date: dayjs(searchDate[0].endDate).format("YYYY-MM-DD"),
                  });
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSearch}
                disabled={loading}
              >
                بحث
              </Button>
              <Tooltip title="تصدير ملف اكسل">
                <IconButton
                  onClick={() => {
                    dispatch(Reports.exportPosEPayments(search));
                  }}
                >
                  <BsFillFileEarmarkExcelFill />
                </IconButton>
              </Tooltip>
              <Tooltip title="الغاء الفلتر">
                <IconButton
                  onClick={() => {
                    const cleared = {
                      start_date: dayjs().format("YYYY-MM-01"),
                      end_date: dayjs().format("YYYY-MM-DD"),
                      j_rep_id: null,
                      search: "",
                      is_debit: null,
                      province_id: null,
                      city_id: [],
                    };
                    setSearch(cleared);
                    setProvinceId(null);
                    dispatch(Reports.getPosEPayments(cleared));
                  }}
                >
                  <MdOutlineClear />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <DataGrid
              rows={
                _PosEPayments.data?.map((x, index) => ({
                  ...x,
                  id: x.user_id || index + 1,
                })) ?? []
              }
              autoHeight
              columns={columns}
              rowCount={_PosEPayments.total}
              loading={loading}
              rowHeight={50}
              selectionModel={[]}
              rowsPerPageOptions={[100]}
              // components={{
              //   Footer: CustomFooter,
              // }}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default PosEPaymentsTable;
